import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Package, Search, Filter, Send, Plus, Minus, Target,
  ChevronDown, CheckSquare, LayoutGrid, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import * as categoryApi from '@/services/categoryApi';

// Searchable Dropdown Component
const SearchableDropdown = ({ 
  label, 
  placeholder, 
  options, 
  value, 
  onChange, 
  disabled, 
  isLoading,
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(opt => opt?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-gray-400'}`}
          onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
          data-testid={testId}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
              {value || placeholder}
            </span>
          )}
          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
        </div>
        
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="pl-8 h-9"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
              ) : (
                filteredOptions.map((opt, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${opt === value ? 'bg-blue-50 text-blue-700' : ''}`}
                    onClick={() => {
                      onChange(opt);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                  >
                    {opt}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryInchargeDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Module selection
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  
  // Closing stock report
  const [report, setReport] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [operationFilter, setOperationFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  
  // Shipment request
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFGModal, setShowFGModal] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  
  // FG Selection (cascading) with loading states
  const [categories, setCategories] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [shades, setShades] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [fgSelection, setFgSelection] = useState({
    category: '',
    design: '',
    size: '',
    shade: '',
    description: ''
  });
  const [fgLookupResults, setFgLookupResults] = useState([]);
  const [selectedFgItem, setSelectedFgItem] = useState(null);
  
  // Loading states for FG selection
  const [isDesignsLoading, setIsDesignsLoading] = useState(false);
  const [isSizesLoading, setIsSizesLoading] = useState(false);
  const [isShadesLoading, setIsShadesLoading] = useState(false);
  const [isDescriptionsLoading, setIsDescriptionsLoading] = useState(false);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  
  // Submit dialog
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitRemarks, setSubmitRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch modules on mount
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const data = await categoryApi.getModules();
        setModules(data.modules || []);
        if (data.modules?.length > 0) {
          setSelectedModule(data.modules[0].code);
        }
      } catch (error) {
        toast.error('Failed to fetch modules');
      }
    };
    fetchModules();
    
    // Fetch categories for FG selection
    const fetchCategories = async () => {
      try {
        const data = await categoryApi.getItemCategories();
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch closing stock when module changes
  const fetchClosingStock = useCallback(async () => {
    if (!selectedModule) return;
    
    try {
      setLoading(true);
      // Load the WHOLE module's stock once. Search is applied client-side
      // (see `searchedReport`), so typing never re-hits this expensive endpoint.
      const data = await categoryApi.getModuleClosingStock(selectedModule);
      // Assign a stable unique id per row so selections + per-row qty edits never collide
      // when multiple rows share the same barcode/design/color/operation (e.g. same item
      // across different source sections or pick slips).
      const rows = (data.report || []).map((r, idx) => ({ ...r, _row_id: `r_${idx}` }));
      setReport(rows);
      setTotalQty(data.total_qty || 0);
    } catch (error) {
      toast.error('Failed to fetch closing stock');
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => {
    fetchClosingStock();
  }, [fetchClosingStock]);

  // Instant, client-side search across the displayed columns — no backend call.
  const searchedReport = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return report;
    return report.filter((r) =>
      `${r.barcode || ''} ${r.design_name || ''} ${r.color || ''} ${r.operation_name || ''}`
        .toLowerCase()
        .includes(q)
    );
  }, [report, searchQuery]);

  // Operation-filtered rows for the table (totals/cards use this too).
  const tableRows = useMemo(
    () => searchedReport.filter((r) => operationFilter === 'all' || r.operation_name === operationFilter),
    [searchedReport, operationFilter]
  );

  // Pagination — render only ~100 rows at a time so the browser never freezes
  // on huge production datasets (tens of thousands of rows).
  const totalPages = Math.max(1, Math.ceil(tableRows.length / PAGE_SIZE));
  const pagedRows = useMemo(
    () => tableRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [tableRows, page]
  );

  // Reset to page 1 whenever the filters or module change.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, operationFilter, selectedModule]);

  // Handle category change for FG selection
  const handleCategoryChange = async (category) => {
    setFgSelection(prev => ({ ...prev, category, design: '', size: '', shade: '' }));
    setDesigns([]);
    setSizes([]);
    setShades([]);
    setFgLookupResults([]);
    setSelectedFgItem(null);
    
    if (category) {
      setIsDesignsLoading(true);
      try {
        const data = await categoryApi.getItemDesigns({ category });
        setDesigns(data.designs || []);
      } catch (error) {
        console.error('Failed to fetch designs:', error);
      } finally {
        setIsDesignsLoading(false);
      }
    }
  };

  // Handle design change for FG selection - with auto-population
  const handleDesignChange = async (design) => {
    setFgSelection(prev => ({ ...prev, design, size: '', shade: '', description: '' }));
    setSizes([]);
    setShades([]);
    setDescriptions([]);
    setFgLookupResults([]);
    setSelectedFgItem(null);
    
    if (design) {
      setIsSizesLoading(true);
      setIsShadesLoading(true);
      setIsDescriptionsLoading(true);
      
      try {
        const [sizesData, shadesData, descriptionsData] = await Promise.all([
          categoryApi.getItemSizes(design),
          categoryApi.getItemShades(design),
          categoryApi.getItemDescriptions(design)
        ]);
        
        const fetchedSizes = sizesData.sizes || [];
        const fetchedShades = shadesData.shades || [];
        const fetchedDescriptions = descriptionsData.descriptions || [];
        
        setSizes(fetchedSizes);
        setShades(fetchedShades);
        setDescriptions(fetchedDescriptions);
        
        // Auto-populate if only one option
        let autoSize = '';
        let autoShade = '';
        let autoDesc = '';
        
        if (fetchedSizes.length === 1) {
          autoSize = fetchedSizes[0];
          setFgSelection(prev => ({ ...prev, size: autoSize }));
          toast.info(`Auto-selected size: ${autoSize}`);
        }
        
        if (fetchedShades.length === 1) {
          autoShade = fetchedShades[0];
          setFgSelection(prev => ({ ...prev, shade: autoShade }));
          toast.info(`Auto-selected shade: ${autoShade}`);
        }
        
        if (fetchedDescriptions.length === 1) {
          autoDesc = fetchedDescriptions[0];
          setFgSelection(prev => ({ ...prev, description: autoDesc }));
          toast.info(`Auto-selected description: ${autoDesc}`);
        }
        
        // Lookup items with auto-selected values
        setIsLookupLoading(true);
        const lookupData = await categoryApi.lookupItem(
          design, 
          autoSize || undefined, 
          autoShade || undefined,
          autoDesc || undefined
        );
        const items = lookupData.items || [];
        setFgLookupResults(items);
        
        // Auto-select if only one item
        if (items.length === 1) {
          setSelectedFgItem(items[0]);
          toast.info(`Found item: ${items[0].barcode}`);
        }
      } catch (error) {
        console.error('Failed to fetch sizes/shades/descriptions:', error);
      } finally {
        setIsSizesLoading(false);
        setIsShadesLoading(false);
        setIsDescriptionsLoading(false);
        setIsLookupLoading(false);
      }
    }
  };

  // Handle size change - with auto-population
  const handleSizeChange = async (size) => {
    setFgSelection(prev => ({ ...prev, size }));
    setSelectedFgItem(null);
    
    if (fgSelection.design && size) {
      setIsLookupLoading(true);
      try {
        const lookupData = await categoryApi.lookupItem(
          fgSelection.design, 
          size, 
          fgSelection.shade || undefined,
          fgSelection.description || undefined
        );
        const items = lookupData.items || [];
        setFgLookupResults(items);
        
        // Auto-select if only one item
        if (items.length === 1) {
          setSelectedFgItem(items[0]);
          toast.info(`Found item: ${items[0].barcode}`);
        }
      } catch (error) {
        console.error('Failed to lookup item:', error);
      } finally {
        setIsLookupLoading(false);
      }
    }
  };

  // Handle shade change - with auto-population
  const handleShadeChange = async (shade) => {
    setFgSelection(prev => ({ ...prev, shade }));
    setSelectedFgItem(null);
    
    if (fgSelection.design) {
      setIsLookupLoading(true);
      try {
        const lookupData = await categoryApi.lookupItem(
          fgSelection.design, 
          fgSelection.size || undefined, 
          shade,
          fgSelection.description || undefined
        );
        const items = lookupData.items || [];
        setFgLookupResults(items);
        
        // Auto-select if only one item
        if (items.length === 1) {
          setSelectedFgItem(items[0]);
          toast.info(`Found item: ${items[0].barcode}`);
        }
      } catch (error) {
        console.error('Failed to lookup item:', error);
      } finally {
        setIsLookupLoading(false);
      }
    }
  };

  // Handle description change - with auto-population
  const handleDescriptionChange = async (description) => {
    setFgSelection(prev => ({ ...prev, description }));
    setSelectedFgItem(null);
    
    if (fgSelection.design) {
      setIsLookupLoading(true);
      try {
        const lookupData = await categoryApi.lookupItem(
          fgSelection.design, 
          fgSelection.size || undefined, 
          fgSelection.shade || undefined,
          description
        );
        const items = lookupData.items || [];
        setFgLookupResults(items);
        
        // Auto-select if only one item
        if (items.length === 1) {
          setSelectedFgItem(items[0]);
          toast.info(`Found item: ${items[0].barcode}`);
        }
      } catch (error) {
        console.error('Failed to lookup item:', error);
      } finally {
        setIsLookupLoading(false);
      }
    }
  };

  // Toggle item selection using unique key
  const toggleItemSelection = (item) => {
    const itemKey = getItemKey(item);
    setSelectedItems(prev => {
      const exists = prev.find(i => getItemKey(i) === itemKey);
      if (exists) {
        return prev.filter(i => getItemKey(i) !== itemKey);
      } else {
        // Add with default shipment qty and target FG = design name
        return [...prev, {
          ...item,
          _key: itemKey, // Store the unique key
          shipment_qty: item.closing_qty,
          target_fg_barcode: null,
          target_fg_name: item.design_name // Default to design name
        }];
      }
    });
  };

  // Update shipment qty for selected item using unique key
  const updateShipmentQty = (itemKey, qty) => {
    setSelectedItems(prev => prev.map(item => 
      getItemKey(item) === itemKey ? { ...item, shipment_qty: Math.max(1, Math.min(qty, item.closing_qty)) } : item
    ));
  };

  // Open FG selection modal
  const openFGModal = async (item) => {
    setCurrentEditItem(item);
    setFgSelection({
      category: '',
      design: '',
      size: '',
      shade: '',
      description: ''
    });
    setDesigns([]);
    setSizes([]);
    setShades([]);
    setDescriptions([]);
    setFgLookupResults([]);
    setSelectedFgItem(null);
    setShowFGModal(true);
    
    // Pre-load all designs (without category filter)
    setIsDesignsLoading(true);
    try {
      const data = await categoryApi.getItemDesigns({});
      setDesigns(data.designs || []);
      
      // Pre-populate with item's design if available
      if (item.design_name && item.design_name !== '-' && item.design_name !== 'Unknown') {
        setFgSelection(prev => ({ ...prev, design: item.design_name }));
        // Trigger design change to load sizes/shades/descriptions
        handleDesignChange(item.design_name);
      }
    } catch (error) {
      console.error('Failed to fetch designs:', error);
    } finally {
      setIsDesignsLoading(false);
    }
  };

  // Confirm FG selection and close modal
  const confirmFGSelection = () => {
    if (!selectedFgItem) {
      toast.warning('Please select an item from the results');
      return;
    }
    
    if (currentEditItem) {
      const itemKey = getItemKey(currentEditItem);
      setSelectedItems(prev => prev.map(item => 
        getItemKey(item) === itemKey
          ? { ...item, target_fg_barcode: selectedFgItem.barcode, target_fg_name: selectedFgItem.design_name }
          : item
      ));
      setShowFGModal(false);
      setCurrentEditItem(null);
      toast.success(`Target FG set: ${selectedFgItem.barcode} - ${selectedFgItem.design_name}`);
    }
  };

  // Submit shipment request
  const handleSubmitRequest = async () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one item');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const requestData = {
        module: selectedModule,
        items: selectedItems.map(item => ({
          barcode: item.barcode,
          design_name: item.design_name,
          color: item.color || '',
          operation_name: item.operation_name,
          closing_qty: item.closing_qty,
          shipment_qty: item.shipment_qty,
          target_fg_barcode: item.target_fg_barcode,
          target_fg_name: item.target_fg_name
        })),
        remarks: submitRemarks || null
      };
      
      const result = await categoryApi.createShipmentRequest(requestData);
      
      toast.success(`Shipment request ${result.request_id} created successfully!`);
      setSelectedItems([]);
      setSubmitRemarks('');
      setShowSubmitDialog(false);
      navigate('/category/incharge/requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shipment request');
    } finally {
      setSubmitting(false);
    }
  };

  // Create unique key for each item (composite of all identifying fields)
  const getItemKey = (item) => item._row_id || `${item.barcode}_${item.design_name}_${item.color}_${item.operation_name}`;

  // Check if item is selected by unique key
  const isSelected = (item) => selectedItems.some(i => getItemKey(i) === getItemKey(item));

  // Get selected item by unique key
  const getSelectedItem = (item) => selectedItems.find(i => getItemKey(i) === getItemKey(item));

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Category Incharge Dashboard</h1>
              <p className="text-sm text-[#E8DCC8]">Create Shipment Requests</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/category/incharge/requests')} className="border-white/20 text-white hover:bg-white/10">
              My Requests
            </Button>
            <Button variant="outline" size="sm" onClick={fetchClosingStock} className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Module Selection & Filters */}
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="w-5 h-5" />
              Select Module & Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Module</Label>
                <Select value={selectedModule} onValueChange={(v) => { setSelectedModule(v); setOperationFilter('all'); }}>
                  <SelectTrigger data-testid="module-select">
                    <SelectValue placeholder="Select Module" />
                  </SelectTrigger>
                  <SelectContent>
                    {modules.map(m => (
                      <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Operation</Label>
                <Select value={operationFilter} onValueChange={setOperationFilter}>
                  <SelectTrigger data-testid="operation-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Operations</SelectItem>
                    {Array.from(new Set(report.map(r => r.operation_name).filter(Boolean))).sort().map(op => (
                      <SelectItem key={op} value={op}>{op}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search barcode, design..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Items Summary */}
        {selectedItems.length > 0 && (
          <Card className="border-green-300 bg-green-50 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CheckSquare className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">{selectedItems.length} items selected</p>
                    <p className="text-sm text-green-600">Total shipment qty: {selectedItems.reduce((sum, i) => sum + i.shipment_qty, 0)}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowSubmitDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-request-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards (filtered) */}
        {(() => {
          const filteredRows = searchedReport.filter(r => operationFilter === 'all' || r.operation_name === operationFilter);
          const filteredQty = filteredRows.reduce((s, r) => s + (r.closing_qty || 0), 0);
          const showFilteredQty = operationFilter !== 'all' || !!searchQuery.trim();
          return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">{showFilteredQty ? (operationFilter === 'all' ? 'Qty (filtered)' : `Qty (${operationFilter})`) : 'Total Stock Qty'}</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{(showFilteredQty ? filteredQty : totalQty).toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Items in Report</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{filteredRows.length}{filteredRows.length !== report.length ? ` / ${report.length}` : ''}</p>
                </div>
                <LayoutGrid className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Module</p>
                  <p className="text-lg font-bold text-[#1A1F2E]">{modules.find(m => m.code === selectedModule)?.name || '-'}</p>
                </div>
                <Target className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
          );
        })()}

        {/* Closing Stock Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : searchedReport.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Stock Found</h3>
              <p className="text-[#5A5F6E]">Select a different module or adjust filters</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Design-wise Closing Stock
                <Badge variant="outline" className="ml-2">
                  {tableRows.length.toLocaleString()} items
                  {operationFilter !== 'all' && <span className="ml-1 text-[10px] text-gray-500">({operationFilter})</span>}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-12">Select</TableHead>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Design Name</TableHead>
                      <TableHead>Color/Shade</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead className="text-center">Closing Qty</TableHead>
                      <TableHead className="text-center">Shipment Qty</TableHead>
                      <TableHead>Target FG</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedRows
                      .map((row, index) => {
                      const selected = isSelected(row);
                      const selectedItem = getSelectedItem(row);
                      const itemKey = getItemKey(row);
                      
                      return (
                        <TableRow 
                          key={row._row_id || `${itemKey}-${index}`} 
                          className={`hover:bg-gray-50 ${selected ? 'bg-green-50' : ''}`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleItemSelection(row)}
                              data-testid={`select-${row.barcode}-${row._row_id || index}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{row.barcode}</TableCell>
                          <TableCell className="font-medium">{row.design_name}</TableCell>
                          <TableCell>{row.color || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{row.operation_name}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-700 border-0">{row.closing_qty}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {selected && selectedItem ? (
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => updateShipmentQty(itemKey, (selectedItem.shipment_qty || 1) - 1)}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <Input
                                  type="number"
                                  value={selectedItem.shipment_qty || 0}
                                  onChange={(e) => updateShipmentQty(itemKey, parseInt(e.target.value) || 0)}
                                  className="w-16 h-7 text-center"
                                  min={1}
                                  max={row.closing_qty}
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => updateShipmentQty(itemKey, (selectedItem.shipment_qty || 1) + 1)}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {selected && selectedItem ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openFGModal(selectedItem);
                                }}
                                className="text-xs"
                                type="button"
                              >
                                {selectedItem.target_fg_name || row.design_name}
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4" data-testid="pagination-controls">
                  <p className="text-sm text-gray-500">
                    Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, tableRows.length)} of {tableRows.length.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="page-prev">Prev</Button>
                    <span className="text-sm whitespace-nowrap">Page {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="page-next">Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* FG Selection Modal */}
        <Dialog open={showFGModal} onOpenChange={setShowFGModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Select Target FG Article
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Cascading Filters with Searchable Dropdowns */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <SearchableDropdown
                    label="Design Name *"
                    placeholder="Search and select design..."
                    options={designs}
                    value={fgSelection.design}
                    onChange={handleDesignChange}
                    disabled={false}
                    isLoading={isDesignsLoading}
                    testId="design-select"
                  />
                </div>
                
                <SearchableDropdown
                  label="Size"
                  placeholder={sizes.length === 0 ? "Select design first" : "Select size..."}
                  options={sizes}
                  value={fgSelection.size}
                  onChange={handleSizeChange}
                  disabled={!fgSelection.design || sizes.length === 0}
                  isLoading={isSizesLoading}
                  testId="size-select"
                />
                
                <SearchableDropdown
                  label="Shade"
                  placeholder={shades.length === 0 ? "Select design first" : "Select shade..."}
                  options={shades}
                  value={fgSelection.shade}
                  onChange={handleShadeChange}
                  disabled={!fgSelection.design || shades.length === 0}
                  isLoading={isShadesLoading}
                  testId="shade-select"
                />
                
                <div className="col-span-2">
                  <SearchableDropdown
                    label="Description"
                    placeholder={descriptions.length === 0 ? "Select design first" : "Select description..."}
                    options={descriptions}
                    value={fgSelection.description}
                    onChange={handleDescriptionChange}
                    disabled={!fgSelection.design || descriptions.length === 0}
                    isLoading={isDescriptionsLoading}
                    testId="description-select"
                  />
                </div>
              </div>
              
              {/* Selected Item Display */}
              {selectedFgItem && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-700 font-medium">Selected Item</p>
                        <p className="text-lg font-bold text-green-800">{selectedFgItem.barcode}</p>
                        <p className="text-sm text-green-600">
                          {selectedFgItem.design_name} | {selectedFgItem.size || 'N/A'} | {selectedFgItem.shade || 'N/A'} | {selectedFgItem.description || 'N/A'}
                        </p>
                      </div>
                      <CheckSquare className="w-8 h-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Loading Indicator */}
              {isLookupLoading && (
                <div className="flex items-center justify-center py-4 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Looking up items...
                </div>
              )}
              
              {/* Lookup Results Table */}
              {!isLookupLoading && fgLookupResults.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Available Items ({fgLookupResults.length})
                  </Label>
                  <div className="border rounded-lg max-h-48 overflow-y-auto overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Barcode</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Shade</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fgLookupResults.map(item => (
                          <TableRow 
                            key={item.barcode} 
                            className={`cursor-pointer ${selectedFgItem?.barcode === item.barcode ? 'bg-green-100' : 'hover:bg-gray-50'}`}
                            onClick={() => setSelectedFgItem(item)}
                          >
                            <TableCell className="font-mono text-sm font-medium">{item.barcode}</TableCell>
                            <TableCell>{item.design_name}</TableCell>
                            <TableCell>{item.size || '-'}</TableCell>
                            <TableCell>{item.shade || '-'}</TableCell>
                            <TableCell className="text-xs">{item.description || '-'}</TableCell>
                            <TableCell>
                              <Button 
                                variant={selectedFgItem?.barcode === item.barcode ? "default" : "outline"} 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFgItem(item);
                                }}
                              >
                                {selectedFgItem?.barcode === item.barcode ? '✓' : 'Select'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* No Results Message */}
              {!isLookupLoading && fgSelection.design && fgLookupResults.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No items found for this combination</p>
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowFGModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={confirmFGSelection}
                disabled={!selectedFgItem}
                className="bg-[#2D5F4F] hover:bg-[#234a3d]"
              >
                Confirm Selection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Submit Request Dialog */}
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Shipment Request</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Module: <span className="font-semibold">{modules.find(m => m.code === selectedModule)?.name}</span></p>
                <p className="text-sm text-gray-600">Items: <span className="font-semibold">{selectedItems.length}</span></p>
                <p className="text-sm text-gray-600">Total Shipment Qty: <span className="font-semibold">{selectedItems.reduce((sum, i) => sum + i.shipment_qty, 0)}</span></p>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Remarks (Optional)</Label>
                <Textarea
                  placeholder="Add any notes or remarks..."
                  value={submitRemarks}
                  onChange={(e) => setSubmitRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmitRequest} 
                disabled={submitting}
                className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CategoryInchargeDashboard;
